import {
  AdminConfigurations,
  OcularMotilityPatternConfiguration,
  SmoothPursuitOrbitConfiguration,
  ReadingAssessmentPageChangeInteraction,
  ReliabilityAutoRestartMetric,
} from "types/adminConfigurationTypes";

export const RELIABILITY_AUTO_RESTART_METRICS: ReliabilityAutoRestartMetric[] = [
  "falsePositive",
  "falseNegative",
  "fixationLoss",
];

export const MIN_RELIABILITY_ERROR_PERCENTAGE = 0;
export const MAX_RELIABILITY_ERROR_PERCENTAGE = 100;
export const MIN_RELIABILITY_PRESENTATIONS = 1;
// Shorter than this and the operator cannot reach Do Not Restart in time.
export const MIN_RELIABILITY_COUNTDOWN_SECONDS = 3;
export const MAX_RELIABILITY_COUNTDOWN_SECONDS = 60;
export const MAX_RELIABILITY_PRESENTATIONS = 200;

export const OCULAR_MOTILITY_FIELD_BOUNDS: Record<
  keyof OcularMotilityPatternConfiguration,
  { min: number; max: number; step: number }
> = {
  patternSize: { min: 0.1, max: 30, step: 0.1 },
  speed: { min: 0.1, max: 20, step: 0.1 },
  pauseDuration: { min: 0, max: 10, step: 0.1 },
};

export const OCULAR_MOTILITY_DECIMAL_PLACES = 2;

export const SMOOTH_PURSUIT_FIELD_BOUNDS: Record<
  keyof SmoothPursuitOrbitConfiguration,
  { min: number; max: number; step: number }
> = {
  depth: { min: 0.5, max: 5, step: 0.1 },
  radiusDegrees: { min: 1, max: 30, step: 0.5 },
  frequencyHz: { min: 0.05, max: 2, step: 0.05 },
  clockwiseDuration: { min: 0, max: 120, step: 1 },
  counterClockwiseDuration: { min: 0, max: 120, step: 1 },
};

export const DEFAULT_ADMIN_CONFIGURATIONS: AdminConfigurations = {
  readingAssessment: {
    pageChangeInteraction: ReadingAssessmentPageChangeInteraction.SingleClick,
  },
  visualFieldAutoRestart: {
    countdownSeconds: 10,
    falsePositive: { minErrorPercentage: 25, minPresentations: 5 },
    falseNegative: { minErrorPercentage: 25, minPresentations: 5 },
    fixationLoss: { minErrorPercentage: 30, minPresentations: 5 },
  },
  ocularMotility: {
    xih: { patternSize: 3.2, speed: 5, pauseDuration: 0.5 },
    // The headset's SmoothPursuitStageConfig defaults, so a save changes nothing.
    smoothPursuit: {
      depth: 1.5,
      radiusDegrees: 10,
      frequencyHz: 0.4,
      clockwiseDuration: 25,
      counterClockwiseDuration: 25,
    },
  },
  visualFieldFixationLoss: {
    movingFixationCatchTrials: true,
  },
};

export const RELIABILITY_AUTO_RESTART_METRIC_LABEL_KEYS: Record<
  ReliabilityAutoRestartMetric,
  string
> = {
  falsePositive: "adminConfigurations.falsePositive",
  falseNegative: "adminConfigurations.falseNegative",
  fixationLoss: "adminConfigurations.fixationLoss",
};

export const SMOOTH_PURSUIT_FIELD_LABEL_KEYS: Record<
  keyof SmoothPursuitOrbitConfiguration,
  string
> = {
  depth: "adminConfigurations.depth",
  radiusDegrees: "adminConfigurations.radiusDegrees",
  frequencyHz: "adminConfigurations.frequencyHz",
  clockwiseDuration: "adminConfigurations.clockwiseDuration",
  counterClockwiseDuration: "adminConfigurations.counterClockwiseDuration",
};

export const SMOOTH_PURSUIT_FIELD_DESCRIPTION_KEYS: Record<
  keyof SmoothPursuitOrbitConfiguration,
  string
> = {
  depth: "adminConfigurations.depthDescription",
  radiusDegrees: "adminConfigurations.radiusDegreesDescription",
  frequencyHz: "adminConfigurations.frequencyHzDescription",
  clockwiseDuration: "adminConfigurations.clockwiseDurationDescription",
  counterClockwiseDuration: "adminConfigurations.counterClockwiseDurationDescription",
};

export const OCULAR_MOTILITY_FIELD_LABEL_KEYS: Record<
  keyof OcularMotilityPatternConfiguration,
  string
> = {
  patternSize: "adminConfigurations.patternSize",
  speed: "adminConfigurations.speed",
  pauseDuration: "adminConfigurations.pauseDuration",
};

export const OCULAR_MOTILITY_FIELD_DESCRIPTION_KEYS: Record<
  keyof OcularMotilityPatternConfiguration,
  string
> = {
  patternSize: "adminConfigurations.patternSizeDescription",
  speed: "adminConfigurations.speedDescription",
  pauseDuration: "adminConfigurations.pauseDurationDescription",
};

export const READING_ASSESSMENT_PAGE_CHANGE_LABEL_KEYS: Record<
  ReadingAssessmentPageChangeInteraction,
  string
> = {
  [ReadingAssessmentPageChangeInteraction.SingleClick]: "adminConfigurations.singleClick",
  [ReadingAssessmentPageChangeInteraction.DoubleClick]: "adminConfigurations.doubleClick",
};
